import { useState, useMemo, useCallback } from 'react';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig<T = string> {
  key: T | null;
  direction: SortDirection;
}

/**
 * Custom hook for managing table sorting state and logic
 * @param initialKey - Initial column key to sort by (optional)
 * @param initialDirection - Initial sort direction (optional)
 * @returns Sorting state and helper functions
 */
export function useSorting<T = string>(
  initialKey: T | null = null,
  initialDirection: SortDirection = null
) {
  const [sortConfig, setSortConfig] = useState<SortConfig<T>>({
    key: initialKey,
    direction: initialDirection,
  });

  /**
   * Toggle sort for a column
   * First click: ascending
   * Second click: descending
   * Third click: reset to null
   */
  const handleSort = useCallback((key: T) => {
    setSortConfig((current) => {
      if (current.key !== key) {
        // New column - start with ascending
        return { key, direction: 'asc' };
      }
      
      // Same column - cycle through asc -> desc -> null
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      } else if (current.direction === 'desc') {
        return { key: null, direction: null };
      } else {
        return { key, direction: 'asc' };
      }
    });
  }, []);

  /**
   * Clear sorting
   */
  const clearSort = useCallback(() => {
    setSortConfig({ key: null, direction: null });
  }, []);

  /**
   * Set specific sort configuration
   */
  const setSort = useCallback((key: T | null, direction: SortDirection) => {
    setSortConfig({ key, direction });
  }, []);

  /**
   * Get sort direction for a specific column key
   */
  const getSortDirection = useCallback((key: T): SortDirection => {
    return sortConfig.key === key ? sortConfig.direction : null;
  }, [sortConfig]);

  /**
   * Check if a column is currently being sorted
   */
  const isColumnSorted = useCallback((key: T): boolean => {
    return sortConfig.key === key && sortConfig.direction !== null;
  }, [sortConfig]);

  return {
    sortConfig,
    handleSort,
    clearSort,
    setSort,
    getSortDirection,
    isColumnSorted,
  };
}

/**
 * Sort an array of objects based on sort configuration
 * @param items - Array to sort
 * @param sortConfig - Sort configuration
 * @param getValueFn - Function to extract sortable value from item
 */
export function applySorting<T, K = string>(
  items: T[],
  sortConfig: SortConfig<K>,
  getValueFn: (item: T, key: K) => any
): T[] {
  if (!sortConfig.key || !sortConfig.direction) {
    return items;
  }

  return [...items].sort((a, b) => {
    const aVal = getValueFn(a, sortConfig.key!);
    const bVal = getValueFn(b, sortConfig.key!);

    // Handle null/undefined values - push to end
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    // String comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      const comparison = aVal.toLowerCase().localeCompare(bVal.toLowerCase());
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    }

    // Number/Date comparison
    if (aVal < bVal) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aVal > bVal) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });
}

export default useSorting;
